// src/components/UsersPage.js
import React, { useEffect, useState } from "react";
import {
  collection, getDocs, doc, getDoc, updateDoc
} from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { db, auth } from "../firebase";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");

  // Load current user + list
  useEffect(() => {
    const load = async () => {
      setLoading(true);

      const current = auth.currentUser;
      let role = null;

      if (current) {
        const snap = await getDoc(doc(db, "users", current.uid));
        role = snap.exists() ? snap.data().role : null;
      }
      setMyRole(role);

      const listSnap = await getDocs(collection(db, "users"));
      const list = listSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const order = { superadmin: 0, admin: 1, staff: 2 };
      list.sort((a, b) => order[a.role] - order[b.role]);

      setUsers(list);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-bold">User Management</h1>
        <p className="text-gray-500 text-sm">Loading users…</p>
      </div>
    );
  }

  if (!myRole || myRole.toLowerCase() !== "superadmin") {
    return (
      <div className="bg-white/95 border border-yellow-200 rounded-xl p-6 shadow-md">
        <h1 className="text-2xl font-bold mb-3">User Management</h1>
        <p className="text-yellow-800">
          You do not have permission to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">User Management</h1>
        <p className="text-gray-600 text-sm">
          Manage roles and send password reset links.
        </p>
      </div>

      {/* Table */}
      <div className="bg-white/95 border border-amber-50 rounded-xl shadow-md overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-orange-50 text-gray-700">
            <tr>
              <th className="p-3">Email</th>
              <th className="p-3">Role</th>
              <th className="p-3">UID</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-orange-50/40">
                <td className="p-3">
                  <div className="font-medium">{u.email}</div>
                  {u.id === auth.currentUser?.uid && (
                    <div className="text-xs text-[#b4642d]">(You)</div>
                  )}
                </td>

                <td className="p-3">
                  <select
                    className="border rounded px-2 py-1 text-sm"
                    value={u.role}
                    disabled={savingId === u.id}
                    onChange={async (e) => {
                      setSavingId(u.id);
                      await updateDoc(doc(db, "users", u.id), {
                        role: e.target.value,
                      });
                      setSavingId(null);
                    }}
                  >
                    <option value="superadmin">SUPERADMIN</option>
                    <option value="admin">ADMIN</option>
                    <option value="staff">STAFF</option>
                  </select>
                </td>

                <td className="p-3 text-xs text-gray-500">
                  {u.id}
                </td>

                <td className="p-3 text-center space-x-2">
                  <button
                    className="text-[#b4642d] hover:underline"
                    onClick={() => {
                      if (!u.email) return alert("No email on file.");
                      const ok = window.confirm(
                        `Send reset email to:\n\n${u.email}?`
                      );
                      if (ok) sendPasswordResetEmail(auth, u.email);
                    }}
                  >
                    Reset Password
                  </button>
                </td>
              </tr>
            ))}

            {users.length === 0 && (
              <tr>
                <td colSpan="4" className="p-4 text-center text-gray-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
